import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { isFullAdminFromStorage } from '../utils/staffAccess';
import './ChatWidget.css';

const API_URL = process.env.REACT_APP_CHAT_ASSISTANT_URL;

/** Si el usuario cerró el panel, no volver a abrirlo solo (timer / nudge). El FAB sigue abriendo a mano. */
const CHAT_DISMISSED_KEY = 'chat_assistant_dismissed';

const isChatDismissed = () => {
  try {
    return localStorage.getItem(CHAT_DISMISSED_KEY) === 'true';
  } catch {
    return false;
  }
};

const markChatDismissed = () => {
  try {
    localStorage.setItem(CHAT_DISMISSED_KEY, 'true');
  } catch (_) {
    /* ignore quota / private mode */
  }
};

const ChatWidget = ({ cart, setCart }) => {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('chat_messages');
      return saved ? JSON.parse(saved) : [
        { role: 'assistant', content: '¡Hola! ¿Cómo estás? ¿En qué podemos ayudarte? Estoy aquí para servirte, mostrarte productos, descuentos, etc.' }
      ];
    } catch {
      return [{ role: 'assistant', content: '¡Hola! ¿Cómo estás? ¿En qué podemos ayudarte? Estoy aquí para servirte, mostrarte productos, descuentos, etc.' }];
    }
  });

  const endRef = useRef(null);
  const [previewsByIndex, setPreviewsByIndex] = useState({});
  const navigate = useNavigate();
  const location = useLocation();
  const [showNudge, setShowNudge] = useState(false);
  const nudgeTimeoutRef = useRef(null);
  const hideTimeoutRef = useRef(null);

  const isFullAdmin = isFullAdminFromStorage();

  // Abrir el chat automáticamente a los 30 s solo si el usuario no lo cerró antes (persistido en localStorage)
  useEffect(() => {
    if (isFullAdmin) return;
    if (isChatDismissed()) return;
    try {
      const saved = localStorage.getItem('chat_messages');
      if (saved) {
        const list = JSON.parse(saved);
        const hasUserMessage = Array.isArray(list) && list.some(m => m && m.role === 'user');
        if (hasUserMessage) return; // ya usó el chat, no abrir automáticamente
      }
    } catch (_) {}
    const t = setTimeout(() => setOpen(true), 30000);
    return () => clearTimeout(t);
  }, [isFullAdmin]);

  useEffect(() => {
    localStorage.setItem('chat_messages', JSON.stringify(messages));
  }, [messages]);

  useEffect(() => {
    if (endRef.current) endRef.current.scrollIntoView({ behavior: 'smooth' });
  }, [messages, open]);

  // Mostrar un aviso después de 30s de navegación (una vez por sesión)
  useEffect(() => {
    // Ocultar si el chat se abre
    if (open) {
      setShowNudge(false);
    }
    if (isChatDismissed()) {
      setShowNudge(false);
      return () => {};
    }
    // Evitar repetir en la misma sesión
    if (sessionStorage.getItem('chat_nudge_shown') === 'true') {
      return () => {};
    }
    // Reiniciar timers al cambiar de ruta
    if (nudgeTimeoutRef.current) clearTimeout(nudgeTimeoutRef.current);
    if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);

    nudgeTimeoutRef.current = setTimeout(() => {
      if (!open && !isChatDismissed()) {
        setShowNudge(true);
        sessionStorage.setItem('chat_nudge_shown', 'true');
        hideTimeoutRef.current = setTimeout(() => setShowNudge(false), 6000);
      }
    }, 30000);

    return () => {
      if (nudgeTimeoutRef.current) clearTimeout(nudgeTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, [location, open]);

  // Extraer enlaces de producto del texto
  const extractProductLinks = (text) => {
    if (!text || typeof text !== 'string') return [];
    const results = [];
    const urlRegex = /https?:\/\/[^\s]+/g;
    const matches = text.match(urlRegex) || [];
    matches.forEach((url) => {
      const m = url.match(/\/producto\/([A-Za-z0-9_-]+)/);
      if (m && m[1]) {
        results.push({ url, productId: m[1] });
      }
    });
    return results;
  };

  // Construir previews (imagen 60x60) solo cuando el panel está abierto
  useEffect(() => {
    if (!open) return;
    const buildPreviews = async () => {
      const newPreviews = { ...previewsByIndex };
      const recent = messages.slice(Math.max(0, messages.length - 30));
      // Cargar Firestore on-demand
      let db, getDoc, doc;
      try {
        const [{ db: importedDb }, firestore] = await Promise.all([
          import('../firebase'),
          import('firebase/firestore')
        ]);
        db = importedDb;
        getDoc = firestore.getDoc;
        doc = firestore.doc;
      } catch (_) {
        return;
      }
      const results = await Promise.allSettled(recent.map(async (m, idxOffset) => {
        const idx = messages.length - recent.length + idxOffset;
        if (m.role !== 'assistant') return;
        if (newPreviews[idx]) return;
        let links = extractProductLinks(m.content);
        // De-duplicar por productId/url
        if (links && links.length > 0) {
          const seen = new Set();
          links = links.filter(l => {
            const key = l.productId || l.url;
            if (!key) return false;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        }
        // Limitar cantidad de previews por respuesta a 4
        if (links.length > 4) links = links.slice(0, 4);
        if (links.length === 0) return;
        const previews = [];
        for (const link of links) {
          try {
            const ref = doc(db, 'products', link.productId);
            const snap = await getDoc(ref);
            if (snap.exists()) {
              const data = snap.data();
              const image = Array.isArray(data.images) && data.images.length > 0 ? data.images[0] : null;
              if (image) {
                const path = `/producto/${link.productId}`;
                const origin = typeof window !== 'undefined' && window.location && window.location.origin ? window.location.origin : '';
                const absolute = origin ? `${origin}${path}` : path;
                // Evitar duplicados de preview
                const already = previews.some(p => p.path === path || p.image === image);
                if (!already) {
                  previews.push({ url: absolute, path, image, title: data.model || 'Producto' });
                }
              }
            }
          } catch (_) {
            // ignore fetch errors for previews
          }
        }
        if (previews.length > 0) {
          newPreviews[idx] = previews;
        }
      }));
      // Opcional: revisar resultados por debugging
      void results;
      setPreviewsByIndex(newPreviews);
    };
    buildPreviews();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [messages, open]);

  // Linkificar texto para que enlaces sean clickeables (para mensajes del usuario u otros casos)
  const linkify = (text, { suppressProductLinks = false } = {}) => {
    if (!text || typeof text !== 'string') return text;
    const parts = [];
    const urlRegex = /https?:\/\/[^\s]+/g;
    let lastIndex = 0;
    let match;
    while ((match = urlRegex.exec(text)) !== null) {
      const url = match[0];
      const start = match.index;
      if (start > lastIndex) parts.push(text.slice(lastIndex, start));
      const isProductUrl = /\/producto\//i.test(url);
      if (suppressProductLinks && isProductUrl) {
        // omitir el enlace de producto en el texto (se mostrará como miniatura aparte)
      } else {
        parts.push(
          <a key={`${start}-${url}`} href={url} target="_blank" rel="noopener noreferrer">{url}</a>
        );
      }
      lastIndex = start + url.length;
    }
    if (lastIndex < text.length) parts.push(text.slice(lastIndex));
    return parts.length ? parts : text;
  };

  // Sanitizar contenido del asistente para ocultar enlaces de productos en el texto
  const sanitizeAssistantText = (text, { suppressEnumerations = false, previewTitles = [] } = {}) => {
    if (!text || typeof text !== 'string') return text;
    let out = text;
    // Quitar sintaxis markdown de enlaces [texto](url)
    out = out.replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '$1');
    // Eliminar URLs de productos explícitas
    out = out.replace(/https?:\/\/[^\s]*\/producto\/[A-Za-z0-9_-]+/g, '');
    // Si hay previews, quitar "Ver producto(s)" solo cuando aparece como frase/sentencia suelta o al final de línea
    const hasPreviews = Array.isArray(previewTitles) && previewTitles.length > 0;
    if (hasPreviews) {
      const byLines = out.split(/\r?\n/);
      const cleanedLines = byLines.map((ln) => {
        const t = ln.trim();
        // Omitir líneas que sean solo "Ver producto(s)" (con o sin viñetas)
        if (/^(?:[\-–—•]\s*)?ver\s+productos?\.?$/i.test(t)) {
          return '';
        }
        // Quitar "Ver producto(s)" al final de la línea
        const removed = t.replace(/(?:\s*[\-–—•]\s*)?ver\s+productos?\.?\s*$/i, '').trimEnd();
        return removed;
      });
      out = cleanedLines.join('\n');
    }
    // Si mostramos miniaturas o navegamos, suprimir enumeraciones tipo "1. NOMBRE - $PRECIO"
    if (suppressEnumerations) {
      const lines = out.split(/\r?\n/);
      const cleaned = lines.filter((line) => {
        const trimmed = line.trim();
        // Remover líneas enumeradas con precio
        if (/^\d+\.|^-\s/.test(trimmed)) {
          if (/\$\s*\d+[\.,\d]*/.test(trimmed)) return false;
        }
        // Remover líneas que sean solo el nombre de productos ya mostrados
        if (previewTitles && previewTitles.length > 0) {
          const matchTitle = previewTitles.some((t) => t && trimmed.toLowerCase() === String(t).toLowerCase());
          if (matchTitle) return false;
        }
        return true;
      });
      out = cleaned.join('\n');
    }
    // De-duplicar líneas idénticas y suprimir frases redundantes como "Ver producto" cuando hay previews
    const lines = out.split(/\r?\n/);
    const seen = new Set();
    const normalized = (s) => s.normalize('NFKD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();
    const result = [];
    for (const line of lines) {
      const trimmed = line.trim();
      if (!trimmed) {
        // Evitar múltiples líneas en blanco seguidas
        if (result.length > 0 && result[result.length - 1] === '') continue;
        result.push('');
        continue;
      }
      let cleanedLine = trimmed;
      if (hasPreviews) {
        // Quitar "ver producto(s)" al final de línea si quedó
        cleanedLine = cleanedLine.replace(/\bver\s+productos?\b[\s\.,;:!¡¿\)]*$/i, '').trim();
      }
      const norm = normalized(cleanedLine);
      // Suprimir "ver producto" redundante si ya hay previews
      if (hasPreviews && (norm === 'ver producto' || norm === 'ver productos')) {
        continue;
      }
      // Suprimir duplicados exactos dentro del mismo mensaje
      if (seen.has(norm)) continue;
      seen.add(norm);
      result.push(cleanedLine);
    }
    out = result.join('\n').replace(/\n{3,}/g, '\n\n');
    return out;
  };

  // Renderizar texto del asistente con párrafos y saltos de línea legibles
  const renderAssistantContent = (text, idx) => {
    const titles = (previewsByIndex[idx] || []).map(p => p.title);
    const cleaned = sanitizeAssistantText(text, {
      suppressEnumerations: Boolean(previewsByIndex[idx]?.length),
      previewTitles: titles,
    }) || '';
    const paragraphs = String(cleaned).split(/\n{2,}/).map(s => s.trim()).filter(Boolean);
    return paragraphs.map((para, pIdx) => {
      const lines = para.split(/\n/);
      return (
        <p key={pIdx} className="chat-paragraph">
          {lines.map((line, lIdx) => (
            <React.Fragment key={lIdx}>
              {linkify(line, { suppressProductLinks: true })}
              {lIdx < lines.length - 1 ? <br /> : null}
            </React.Fragment>
          ))}
        </p>
      );
    });
  };

  const send = async (text) => {
    const content = text ?? input;
    if (!content?.trim()) return;
    const newMessages = [...messages, { role: 'user', content }];
    setMessages(newMessages);
    setInput('');
    setIsLoading(true);

    try {
      if (!API_URL) {
        setMessages(prev => [...prev, { role: 'assistant', content: 'Configuración faltante: REACT_APP_CHAT_ASSISTANT_URL no está definida.' }]);
        return;
      }
      const resp = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: newMessages.map(m => ({ role: m.role, content: m.content })) })
      });
      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const msg = typeof data?.error === 'string' ? data.error : 'Error en el servidor';
        setMessages(prev => [...prev, { role: 'assistant', content: `Error del servidor: ${msg}` }]);
        return;
      }

      // data puede ser { reply, actions? }
      const replyText = typeof data?.reply === 'string' ? data.reply : 'Listo.';
      setMessages(prev => [...prev, { role: 'assistant', content: replyText }]);

      // Ejecutar acciones soportadas
      const actions = Array.isArray(data?.actions) ? data.actions : [];
      for (const action of actions) {
        if (!action || typeof action !== 'object') continue;
        if (action.type === 'openUrl' && action.url) {
          try {
            const url = new URL(action.url, window.location.origin);
            const sameOrigin = url.origin === window.location.origin;
            if (sameOrigin) {
              navigate(url.pathname + url.search + url.hash);
            } else {
              // Abrir enlaces externos SIEMPRE en la misma pestaña
              window.location.assign(url.toString());
            }
          } catch (_) {
            if (action.url.startsWith('/')) {
              navigate(action.url);
            } else {
              window.location.assign(action.url);
            }
          }
        }
        if (action.type === 'goToCart') {
          window.location.href = '/carrito';
        }
        if (action.type === 'goToCategory' && action.path) {
          navigate(action.path);
        }
        if (action.type === 'goToRoute' && action.path) {
          navigate(action.path);
        }
        // No se soporta agregar al carrito desde el chat
      }
    } catch (e) {
      setMessages(prev => [...prev, { role: 'assistant', content: 'No pude conectarme en este momento.' }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Si el usuario es administrador, no renderizar el componente
  if (isFullAdmin) {
    return null;
  }

  return (
    <>
      <button
        className="chat-fab"
        onClick={() => {
          setOpen((v) => {
            if (v) markChatDismissed();
            return !v;
          });
        }}
        aria-label={open ? 'Cerrar chat' : 'Abrir chat'}
        title="Preguntarle al chatbot"
      >
        {open ? '×' : '💬'}
      </button>

      {!open && showNudge && (
        <div
          className="chat-nudge"
          role="status"
          onClick={() => { setOpen(true); setShowNudge(false); }}
        >
          ¿Puedo ayudarte en algo?
        </div>
      )}

      {open && (
        <div className="chat-panel" role="dialog" aria-label="Chat Asistente Priball">
          <div className="chat-header">
            <strong>Asistente Priball</strong>
            <button
              type="button"
              className="chat-close"
              onClick={() => {
                markChatDismissed();
                setOpen(false);
              }}
              aria-label="Cerrar chat"
              title="Cerrar"
            >
              ×
            </button>
          </div>

          <div className="chat-messages" aria-live="polite">
            {messages.map((m, idx) => (
              <div key={idx} className={`chat-message ${m.role === 'user' ? 'user' : 'bot'}`}>
                {m.role === 'assistant'
                  ? renderAssistantContent(m.content, idx)
                  : linkify(m.content)}
                {m.role === 'assistant' && previewsByIndex[idx] && (
                  <div className="link-previews">
                    {previewsByIndex[idx].map((p, i) => (
                      <button
                        key={i}
                        className="link-preview"
                        title={p.title}
                        onClick={(e) => { e.preventDefault(); navigate(p.path); }}
                        style={{ background: 'transparent', border: 'none', padding: 0, cursor: 'pointer' }}
                      >
                        <img src={p.image} alt={p.title} width={60} height={60} />
                        <span className="link-preview-title">{p.title}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {isLoading && <div className="chat-message bot">Escribiendo…</div>}
            <div ref={endRef} />
          </div>

          <div className="chat-input">
            <input
              type="text"
              placeholder="Escribí tu mensaje…"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') send(); }}
              disabled={isLoading}
            />
            <button onClick={() => send()} disabled={isLoading || !input.trim()}>Enviar</button>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatWidget;


